import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Pickaxe, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Mining = () => {
  const [minerAddress, setMinerAddress] = useState('');
  const [mining, setMining] = useState(false);
  const [pendingTxs, setPendingTxs] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [pendingRes, statsRes] = await Promise.all([
        axios.get(`${API}/pending-transactions`),
        axios.get(`${API}/stats`)
      ]);
      setPendingTxs(pendingRes.data.pending_transactions);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const mineBlock = async () => {
    if (!minerAddress) {
      toast.error('Please enter a miner address');
      return;
    }

    if (pendingTxs.length === 0) {
      toast.error('No pending transactions to mine');
      return;
    }

    try {
      setMining(true);
      const response = await axios.post(`${API}/mine`, {
        miner_address: minerAddress
      });
      toast.success(`Block #${response.data.block.index} mined successfully!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mining failed');
      console.error(error);
    } finally {
      setMining(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-unbounded text-4xl font-bold text-text-primary mb-2">
          Mining Station
        </h1>
        <p className="text-text-secondary">Mine blocks and earn rewards</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass p-6 rounded-sm"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-accent-warning/10 rounded-sm">
              <Pickaxe size={28} className="text-accent-warning" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-unbounded text-2xl font-bold text-text-primary">Mine Block</h2>
              <p className="text-text-muted text-sm">Process pending transactions</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-text-secondary text-sm mb-2 block font-medium">
                Miner Address
              </label>
              <input
                type="text"
                value={minerAddress}
                onChange={(e) => setMinerAddress(e.target.value)}
                placeholder="Enter your wallet address to receive rewards"
                data-testid="miner-address-input"
                className="w-full p-3 bg-background-subtle border border-border rounded-sm text-text-primary focus:border-primary focus:outline-none font-mono text-sm"
              />
            </div>

            <div className="p-4 bg-background-subtle border border-border rounded-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-text-muted text-sm">Pending Transactions</span>
                <span className="font-bold text-primary text-xl" data-testid="pending-tx-count">
                  {pendingTxs.length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-muted text-sm">Mining Reward</span>
                <span className="font-bold text-accent-success text-xl">
                  {stats?.mining_reward || 10} NATIVE
                </span>
              </div>
            </div>

            <button
              onClick={mineBlock}
              disabled={mining || pendingTxs.length === 0}
              data-testid="mine-block-btn"
              className="w-full py-4 bg-accent-warning text-black font-bold rounded-sm hover:opacity-90 transition-opacity duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {mining ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black" />
                  Mining...
                </>
              ) : (
                <>
                  <Zap size={20} />
                  Start Mining
                </>
              )}
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass p-6 rounded-sm"
        >
          <h2 className="font-unbounded text-xl font-bold text-text-primary mb-4">
            Pending Transactions
          </h2>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {pendingTxs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-text-muted">No pending transactions</p>
              </div>
            ) : (
              pendingTxs.map((tx, idx) => (
                <div
                  key={idx}
                  data-testid={`pending-tx-${idx}`}
                  className="p-4 bg-background-subtle border border-border rounded-sm"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-sm font-medium">
                      {tx.tx_type}
                    </span>
                    <span className="font-bold text-accent-success">
                      {tx.amount} {tx.token_symbol}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-text-muted">
                      From:{' '}
                      <span className="font-mono text-text-secondary">
                        {tx.sender.substring(0, 16)}...
                      </span>
                    </p>
                    <p className="text-xs text-text-muted">
                      To:{' '}
                      <span className="font-mono text-text-secondary">
                        {tx.recipient.substring(0, 16)}...
                      </span>
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>
      </div>

      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass p-6 rounded-sm mt-6"
        >
          <h2 className="font-unbounded text-xl font-bold text-text-primary mb-4">
            Network Statistics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-text-muted text-sm mb-1">Difficulty</p>
              <p className="font-bold text-2xl text-primary">{stats.difficulty}</p>
            </div>
            <div>
              <p className="text-text-muted text-sm mb-1">Total Blocks</p>
              <p className="font-bold text-2xl text-accent-success">{stats.total_blocks}</p>
            </div>
            <div>
              <p className="text-text-muted text-sm mb-1">Total Transactions</p>
              <p className="font-bold text-2xl text-accent-info">{stats.total_transactions}</p>
            </div>
            <div>
              <p className="text-text-muted text-sm mb-1">Chain Valid</p>
              <p className="font-bold text-2xl text-accent-success">
                {stats.is_valid ? 'YES' : 'NO'}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Mining;
